<template>
  <div class="relative" ref="root">
    <!--
      AB#21297 — `compact` renders the trigger as an icon button with the label
      in its tooltip and aria-label, for toolbars where three labelled buttons
      cost more than they say. The default stays the labelled button every
      other page renders today.
    -->
    <button
      type="button"
      :class="[
        compact
          ? 'dash-icon-btn relative'
          : 'flex items-center gap-2 border border-primary text-primary rounded-md px-3 py-2 text-sm whitespace-nowrap',
        { 'bg-primary/10': !compact && hidden.length > 0 },
      ]"
      :title="
        hidden.length > 0
          ? `${visibleCount} of ${options.length + alwaysVisibleCount} columns shown`
          : compact
            ? 'Columns'
            : 'Choose which columns this table shows'
      "
      :aria-label="compact ? 'Columns' : undefined"
      :aria-expanded="open"
      aria-haspopup="true"
      @click.stop="open = !open"
    >
      <ViewColumnsIcon class="size-5" />
      <template v-if="!compact">Columns</template>
      <span
        v-if="hidden.length > 0"
        :class="
          compact
            ? 'dash-icon-badge'
            : 'bg-primary-light text-primary text-xs font-bold rounded-[4px] px-1.5 py-0.5'
        "
      >
        {{ visibleCount }}
      </span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 mt-1 z-[10000] bg-surface text-ink rounded-lg shadow-lg border border-line w-64 max-h-[60vh] overflow-y-auto"
    >
      <div class="flex items-center justify-between px-3 py-2 border-b border-line-soft">
        <span class="text-sm font-semibold">Columns</span>
        <button
          v-if="hidden.length > 0"
          type="button"
          class="text-xs text-primary bg-transparent border-none cursor-pointer"
          @click.stop="showAll"
        >
          Show all
        </button>
      </div>

      <ul class="py-1 list-none m-0 p-0">
        <li v-for="label in options" :key="label">
          <label
            class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-2 select-none"
          >
            <input
              type="checkbox"
              class="size-4 cursor-pointer"
              :checked="!hidden.includes(label)"
              :disabled="isLastVisible(label)"
              :title="isLastVisible(label) ? 'At least one column has to stay' : undefined"
              @change="toggle(label)"
            />
            <span class="truncate" :title="label">{{ label }}</span>
          </label>
        </li>
      </ul>

      <!--
        AB#21576 — one way back to what the code ships: every column shown at
        the width it was designed with. Disabled, not hidden, when there is
        nothing to undo, so people learn where it lives before they need it.
      -->
      <div class="border-t border-line-soft px-3 py-2">
        <button
          type="button"
          class="dash-btn w-full justify-center"
          :disabled="!canReset"
          :title="canReset ? 'Show every column at its original width' : 'Nothing has been changed'"
          data-test="columns-reset"
          @click.stop="reset"
        >
          <ArrowUturnLeftIcon class="size-4" />
          Reset columns to default
        </button>
      </div>

      <p class="px-3 py-2 text-xs text-ink-mute border-t border-line-soft m-0">
        Saved for you on this browser. Columns you do not have access to are not listed.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * AB#21240 — per-user column visibility for the orders dashboard.
 *
 * This picker only ever subtracts from what the caller passes in, and the
 * caller passes in what survived the permission gates. So a column someone is
 * not allowed to see can never be offered here, and re-showing everything can
 * never reveal more than they already had.
 */
import { ArrowUturnLeftIcon, ViewColumnsIcon } from '@heroicons/vue/24/outline';
import { onClickOutside } from '@vueuse/core';
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Labels the user is allowed to hide, in table order. */
    options: string[];
    /** Labels currently hidden. */
    hidden: string[];
    /** Columns that are always shown and never offered here (Order No, Actions). */
    alwaysVisibleCount?: number;
    /** Icon-only trigger with the label in the tooltip (AB#21297). */
    compact?: boolean;
    /**
     * AB#21576 — whether "Reset columns to default" has anything to undo. The
     * page knows about widths as well as hidden columns; the picker only sees
     * the latter, so the page decides. Defaults to "a column is hidden".
     */
    canReset?: boolean;
  }>(),
  { alwaysVisibleCount: 0, compact: false, canReset: undefined }
);

const emit = defineEmits<{
  (e: 'update:hidden', value: string[]): void;
  /** Reset requested: the page clears its widths, the picker shows every column. */
  (e: 'reset'): void;
}>();

const canReset = computed(() => props.canReset ?? props.hidden.length > 0);

const reset = () => {
  if (!canReset.value) return;
  emit('update:hidden', []);
  emit('reset');
  open.value = false;
};

const root = ref<HTMLElement | null>(null);
const open = ref(false);

onClickOutside(root, () => {
  open.value = false;
});

const visibleCount = computed(
  () => props.options.length - props.hidden.length + props.alwaysVisibleCount
);

/**
 * The last remaining hideable column cannot be turned off.
 *
 * An empty table reads as broken, and if the picker were ever rendered inside
 * the table's own header the user could not get back to it to undo the choice.
 */
const isLastVisible = (label: string): boolean =>
  !props.hidden.includes(label) && props.options.length - props.hidden.length <= 1;

const toggle = (label: string) => {
  if (props.hidden.includes(label)) {
    emit(
      'update:hidden',
      props.hidden.filter((entry) => entry !== label)
    );
    return;
  }
  if (isLastVisible(label)) return;
  emit('update:hidden', [...props.hidden, label]);
};

const showAll = () => emit('update:hidden', []);
</script>
